// components/ui/HistoryView.jsx
import React, { useState, useMemo, useEffect } from 'react'; // Added useEffect
import { ListGroup, Button, Badge, InputGroup, Form, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import './HistoryView.css';

const HistoryView = ({
    items = [],
    isLoading,
    error,
    onSelectItem,
    onHideItem,
    renderItemSummary,
    searchPlaceholder = "Search history...",
    itemTypePlural = "items",
    filterFunction
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [hoveredItemId, setHoveredItemId] = useState(null);

    // Log when items prop changes
    useEffect(() => {
        console.log(`HistoryView: items prop updated. Length: ${items?.length || 0}`, items);
    }, [items]);

    const filteredItems = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return items;

        const deepSearch = (obj, term) => {
            if (typeof obj === 'string') return obj.toLowerCase().includes(term);
            if (typeof obj === 'number') return obj.toString().includes(term);
            if (Array.isArray(obj)) return obj.some(el => deepSearch(el, term));
            if (typeof obj === 'object' && obj !== null)
                return Object.values(obj).some(value => deepSearch(value, term));
            return false;
        };

        return items.filter(item => {
            const target = item.result?.scanImage || item.result || item;
            return deepSearch(target, term);
        });
    }, [items, searchTerm, filterFunction]);

    // Log filtered items
    useEffect(() => {
        console.log(`HistoryView: filteredItems updated. Length: ${filteredItems?.length || 0}`, filteredItems);
    }, [filteredItems]);


    if (isLoading) return <div className="text-center my-4"><Spinner animation="border" /> Loading...</div>;
    if (error) return <Alert variant="danger">{error}</Alert>;

    return (
        <>
            <InputGroup className="mb-3 mt-0 shadow-sm"> {/* Removed mt-2 to be closer to header */}
                <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
                <Form.Control
                    type="search"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </InputGroup>

            {!filteredItems || filteredItems.length === 0 ? (
                <Alert variant="light" className="text-center text-muted py-4"> {/* More subtle alert */}
                    {searchTerm ? `No ${itemTypePlural.toLowerCase()} found matching "${searchTerm}".` : `No ${itemTypePlural.toLowerCase()} history found.`}
                </Alert>
            ) : (
                <ListGroup variant="flush" className="history-list-group">
                    {filteredItems.map((item, index) => { // Added index for logging
                        // Log each item being rendered
                        // console.log(`HistoryView: Rendering item ${index}`, item);
                        const summary = renderItemSummary(item);
                        // if (!summary) {
                        //     console.warn(`HistoryView: renderItemSummary returned null/undefined for item ${index}`, item);
                        // }
                        return (
                            <div
                                key={item.id || item.timestamp || `history-item-${index}`} // More robust key
                                onMouseEnter={() => setHoveredItemId(item.id)}
                                onMouseLeave={() => setHoveredItemId(null)}
                                className="history-item-wrapper"
                            >
                                <ListGroup.Item
                                    action
                                    onClick={() => onSelectItem(item)}
                                    className="d-flex justify-content-between align-items-start history-item-content"
                                >
                                    <div className="ms-2 me-auto">
                                        {summary}
                                    </div>
                                    <Badge bg="light" text="dark" pill className="ms-3 align-self-center">
                                        {item.timestamp || item.created_at ? new Date(item.timestamp || item.created_at).toLocaleDateString() : 'N/A'}
                                    </Badge>
                                </ListGroup.Item>
                                {hoveredItemId === item.id && onHideItem && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="delete-item-btn"
                                        onClick={(e) => { e.stopPropagation(); onHideItem(item.id, e); }}
                                        title={`Hide this ${itemTypePlural.singular || 'item'}`}
                                    >
                                        <FontAwesomeIcon icon={faTrashAlt} />
                                    </Button>
                                )}
                            </div>
                        );
                    })}
                </ListGroup>
            )}
        </>
    );
};

export default HistoryView;